import BaseObjectModelEntry from "~/models/objectModelEntries/baseObjectModelEntry";
import ConditionalRuleSet from "~/models/conditionalRules/conditionalRuleSet";
import {Hash} from "~/interfaces/hashInterfaces";
import {ObjectHelper} from "~/helpers/objectHelper";
import ConditionalRule from "~/models/conditionalRules/conditionalRule";
import {ComparisonType} from "~/models/conditionalRules/comparisonType";
import {DatetimeComparison} from "~/models/conditionalRules/datetimeComparison";
import DatetimeHelper from "~/helpers/datetimeHelper";
import {TargetType} from "~/models/conditionalRules/targetType";
import NumericValidator from "~/models/validators/numericValidator";
import {MultiChoiceComparison} from "~/models/conditionalRules/multiChoiceComparison";
import {TextComparison} from "~/models/conditionalRules/textComparison";
import {JoinType} from "~/models/conditionalRules/joinType";

export default class ObjectModelConditionalRuleEvaluator {
  /**
   * Evaluates the conditional rule set against an object model entry.
   * Does not currently handle formula evaluation.
   *
   * @param entry The entry to pull referenced values from.
   * @param model The model (e.g. object model action, list view content element, etc.)
   * @param fieldName The name of the conditional field.
   * @return The result of evaluating the rule set.
   */
  static evaluate(entry: BaseObjectModelEntry, model: any, fieldName: string): any {
    const ruleSetsHash: Hash<ConditionalRuleSet> = model[`${fieldName}_conditional_rule_sets`];
    const ruleSets = ObjectHelper.toArray(ruleSetsHash);
    for (const set of ruleSets) {
      if (this.ruleSetSatisfied(entry, set)) {
        return set.conditional_value;
      }
    }
    return model[fieldName];
  }

  private static ruleSetSatisfied(entry: BaseObjectModelEntry, ruleSet: ConditionalRuleSet): boolean {
    const children = ObjectHelper.toArray(ruleSet.conditional_rules);
    const isOr = ruleSet.join_type === JoinType.or;
    const comparer: (a: any, b: any) => boolean = isOr ? (a, b) => a || b : (a, b) => a && b;
    return children.reduce((acc, ruleOrSet) => {
      const isSatisfied = ruleOrSet instanceof ConditionalRuleSet ? this.ruleSetSatisfied(entry, ruleOrSet) : this.ruleSatisfied(entry, ruleOrSet);
      return comparer(acc, isSatisfied);
    }, !isOr);
  }

  private static ruleSatisfied(entry: BaseObjectModelEntry, rule: ConditionalRule): boolean {
    switch (rule.comparison_type) {
      case ComparisonType.datetime:
        return this.datetimeRuleSatisfied(entry, rule);
      case ComparisonType.instanceCount:
        return this.instanceCountRuleSatisfied(entry, rule);
      case ComparisonType.multiChoice:
        return this.multiChoiceRuleSatisfied(entry, rule);
      case ComparisonType.singleChoice:
        return this.singleChoiceRuleSatisfied(entry, rule);
      case ComparisonType.numeric:
        return this.numericRuleSatisfied(entry, rule);
      case ComparisonType.text:
        return this.textRuleSatisfied(entry, rule);
    }
  }

  private static datetimeRuleSatisfied(entry: BaseObjectModelEntry, rule: ConditionalRule): boolean {
    const dateValue = DatetimeHelper.parseDate(rule.value).getTime();
    const referencedValue = DatetimeHelper.parseDate(this.referencedValue(entry, rule)).getTime();
    switch (rule.comparison) {
      case DatetimeComparison.isBefore:
        return referencedValue < dateValue;
      case DatetimeComparison.isAfter:
        return referencedValue > dateValue;
      default:
        return referencedValue === dateValue;
    }
  }

  private static instanceCountRuleSatisfied(entry: BaseObjectModelEntry, rule: ConditionalRule): boolean {
    return false;
  }

  private static multiChoiceRuleSatisfied(entry: BaseObjectModelEntry, rule: ConditionalRule): boolean {
    const referencedValue = this.referencedValue(entry, rule);
    if (!referencedValue) return false

    if (rule.target_type === TargetType.count) {
      const count = referencedValue.length;
      return NumericValidator.isValid(count, rule.comparison, parseFloat(rule.value));
    } else {
      const includes = referencedValue.includes(rule.value);
      return rule.comparison === MultiChoiceComparison.includes ? includes : !includes;
    }
  }

  private static singleChoiceRuleSatisfied(entry: BaseObjectModelEntry, rule: ConditionalRule): boolean {
    const selected = this.referencedValue(entry, rule);
    return selected?.length === 1 && selected[0] === rule.value;
  }

  private static numericRuleSatisfied(entry: BaseObjectModelEntry, rule: ConditionalRule): boolean {
    const referencedValue = parseFloat(this.referencedValue(entry, rule));
    return NumericValidator.isValid(referencedValue, rule.comparison, parseFloat(rule.value));
  }

  private static textRuleSatisfied(entry: BaseObjectModelEntry, rule: ConditionalRule): boolean {
    const referencedValue = this.referencedValue(entry, rule);
    switch (rule.comparison) {
      case TextComparison.is:
        return referencedValue === rule.value;
      case TextComparison.isNot:
        return referencedValue !== rule.value;
      case TextComparison.contains:
        return referencedValue.includes(rule.value);
      case TextComparison.doesNotContain:
        return !referencedValue.includes(rule.value);
      case TextComparison.startsWith:
        return referencedValue.startsWith(rule.value);
      case TextComparison.doesNotStartWith:
        return !referencedValue.startsWith(rule.value);
      case TextComparison.endsWith:
        return referencedValue.endsWith(rule.value);
      case TextComparison.doesNotEndWith:
        return !referencedValue.endsWith(rule.value);
    }
  }

  private static referencedValue(entry: BaseObjectModelEntry, rule: ConditionalRule): any {
    return entry.valueFor(rule.referenced_control_id);
  }
}